// Signin page — offers BOTH Google OAuth and email+password in one place.
// Tabs switch between Sign In and Create Account.

import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Loader2, Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

const Signin = () => {
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [submitting, setSubmitting] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast.error("Email and password are required");
      return;
    }
    if (mode === "signup" && form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const payload = mode === "signup"
        ? { email: form.email.trim(), password: form.password, name: form.name.trim() || undefined }
        : { email: form.email.trim(), password: form.password };
      const url = mode === "signup" ? `${API}/auth/signup` : `${API}/auth/signin`;
      const res = await axios.post(url, payload, { withCredentials: true });
      setUser(res.data);
      toast.success(mode === "signup" ? "Welcome! Your trial has started." : "Signed in");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="signin-home-link"><Logo size={28} /></Link>
          <Link to="/preview" className="text-sm text-zinc-600 hover:text-zinc-900" data-testid="signin-preview-link">See the demo →</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-zinc-200 rounded p-8 shadow-sm">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mb-1">
              {mode === "signin" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="text-sm text-zinc-500 mb-6">
              {mode === "signin" ? "Sign in to your roster.ai account." : "Free 30-day trial — no credit card required."}
            </p>

            <button
              onClick={handleGoogle}
              className="w-full py-2.5 px-4 border border-zinc-300 rounded text-sm font-medium text-zinc-700 bg-white hover:bg-zinc-50 inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="google-oauth-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </button>

            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-zinc-200" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">or</span>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === "signup" && (
                <div>
                  <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1"><UserIcon className="w-3 h-3" /> Name (optional)</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded text-sm bg-white"
                    data-testid="signup-name-input"
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded text-sm bg-white"
                  data-testid={`${mode}-email-input`}
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Password</label>
                <input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={mode === "signup" ? "At least 8 characters" : ""}
                  className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded text-sm bg-white"
                  data-testid={`${mode}-password-input`}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  minLength={mode === "signup" ? 8 : undefined}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 px-4 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid={`${mode}-submit-btn`}
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {mode === "signup" ? "Creating…" : "Signing in…"}</>
                  : <>{mode === "signup" ? "Create account" : "Sign in"} <ArrowRight className="w-4 h-4" /></>}
              </button>
              {mode === "signin" && (
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-[#002FA7]" data-testid="signin-forgot-link">
                    Forgot your password?
                  </Link>
                </div>
              )}
            </form>

            <div className="mt-6 text-center text-sm text-zinc-500">
              {mode === "signin" ? (
                <>Don't have an account?{" "}
                  <button type="button" onClick={() => setMode("signup")} className="text-[#002FA7] hover:underline font-medium" data-testid="switch-to-signup">
                    Sign up
                  </button>
                </>
              ) : (
                <>Already have an account?{" "}
                  <button type="button" onClick={() => setMode("signin")} className="text-[#002FA7] hover:underline font-medium" data-testid="switch-to-signin">
                    Sign in
                  </button>
                </>
              )}
            </div>
          </div>

          <p className="text-xs text-zinc-400 text-center mt-4">
            By continuing you agree to our terms of service.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Signin;
